import type { Edge, Node } from '@xyflow/react';
import type { IBaseData } from '@shared/production-editor';
import type { ProductionData } from './types';

/* Production-editor edge styles (hex allowed: graph rendering, mirrors Nemo). */
const EDGE_RED = { stroke: '#E1352B', strokeWidth: 3 };
const EDGE_GREY_DASHED = { stroke: 'var(--nj-core-color-grey-400)', strokeWidth: 1, strokeDasharray: '4 4' };

const UNIT_ENTITY: Record<string, { entityName: string; entityLabel: string }> = {
    boiler: { entityName: 'Boiler', entityLabel: 'Chaudière' },
    'heat-pump': { entityName: 'Chiller', entityLabel: 'Pompe à chaleur' },
    exchanger: { entityName: 'WaterCooling', entityLabel: 'Échangeur' },
    geothermal: { entityName: 'Geothermal', entityLabel: 'Unité de géothermie' },
};

/**
 * Builds an offline graph from the captured site entities (positions are illustrative):
 * fuel contracts → units → collector → pump group → departure, return → units, power contract → pump group.
 */
export function buildSiteGraph(data: ProductionData, siteName: string): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const site = data.sites.find((s) => s.name === siteName) ?? data.sites[0];
    const units = site.units.map((name) => data.units.find((unit) => unit.site === site.name && unit.name === name) ?? {
        name,
        kind: name === 'TEST V2' ? 'heat-pump' : 'geothermal',
        fuelContract: name === 'TEST V2' ? 'Centrale_Default' : null,
    });
    const contracts = [...new Set(units.map((u) => u.fuelContract).filter((c): c is string => !!c))];
    const pump = data.pumps.find((p) => p.site === site.name);
    const unitY = (i: number) => 170 + i * 170;
    const midY = unitY((units.length - 1) / 2);

    const nodes: Node<IBaseData>[] = [
        ...contracts.map((c, i) => ({
            id: `fuel-${c}`,
            type: 'contract',
            position: { x: 220 + i * 190, y: -20 },
            data: { entityName: 'FuelContract', entityLabel: 'Contrat de combustible', label: c },
            width: 165,
            height: 130,
        })),
        {
            id: 'return',
            type: 'asset',
            position: { x: 0, y: midY },
            data: { entityName: 'Return', entityLabel: 'Retour', label: site.name === 'Chaufferie biomasse' ? 'retour 2' : site.departure },
            width: 140,
            height: 150,
        },
        ...units.map((u, i) => ({
            id: `unit-${u.name}`,
            type: 'asset',
            position: { x: 260, y: unitY(i) },
            data: { ...UNIT_ENTITY[u.kind], label: u.name },
            width: 170,
            height: 150,
        })),
        {
            id: 'collector',
            type: 'asset',
            position: { x: 500, y: midY + 30 },
            data: { entityName: 'Collector', entityLabel: 'Collecteur', label: 'Collecteur' },
            width: 120,
            height: 90,
        },
        {
            id: 'pump-group',
            type: 'group',
            position: { x: 680, y: midY - 40 },
            data: { entityName: 'PumpGroup', entityLabel: 'Groupe de pompes', label: site.pumpGroup },
            style: { width: 280, height: 235 },
        },
        ...site.pumps.map((p) => ({
            id: `pump-${p}`,
            type: 'groupMember',
            parentId: 'pump-group',
            extent: 'parent' as const,
            position: { x: 30, y: 60 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe à vitesse variable', label: p },
            width: 220,
            height: 130,
        })),
        {
            id: 'departure',
            type: 'asset',
            position: { x: 1020, y: midY },
            data: { entityName: 'Departure', entityLabel: 'Départ', label: site.departure },
            width: 140,
            height: 150,
        },
    ];
    if (pump) {
        nodes.push({
            id: 'power',
            type: 'contract',
            position: { x: 740, y: midY + 260 },
            data: { entityName: 'PowerContract', entityLabel: "Contrat d'électricité", label: pump.powerContract },
            width: 160,
            height: 130,
        });
    }

    const edges: Edge[] = [
        ...units.flatMap((u) => [
            { id: `e-ret-${u.name}`, source: 'return', target: `unit-${u.name}`, type: 'step', style: EDGE_RED, sourceHandle: 'right', targetHandle: 'left' },
            { id: `e-${u.name}-coll`, source: `unit-${u.name}`, target: 'collector', type: 'step', style: EDGE_RED, sourceHandle: 'right', targetHandle: 'left' },
            ...(u.fuelContract
                ? [{ id: `e-fuel-${u.name}`, source: `fuel-${u.fuelContract}`, target: `unit-${u.name}`, type: 'step', style: EDGE_GREY_DASHED, sourceHandle: 'bottom', targetHandle: 'top' }]
                : []),
        ]),
        { id: 'e-coll-pg', source: 'collector', target: 'pump-group', type: 'step', style: EDGE_RED, sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-pg-dep', source: 'pump-group', target: 'departure', type: 'step', style: EDGE_RED, sourceHandle: 's-right', targetHandle: 'left' },
        ...(pump ? [{ id: 'e-pow-pg', source: 'power', target: 'pump-group', type: 'step', style: EDGE_GREY_DASHED, sourceHandle: 'top', targetHandle: 'bottom' }] : []),
    ];

    return { nodes, edges };
}
